import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { useInvalidateChannelsList } from '@/entities/channel';
import { ApiError } from '@/shared/api/ApiError';
import {
  privateSendTgPersonalPassword,
  sendTgPersonalPassword,
} from '@/shared/api/channels';
import { IConfirmPasswordTelegramPersonalDto } from '@/shared/api/channels/types';
import { useNotifications } from '@/shared/ui/Notifications';

export function useConfirmPassword() {
  const invalidateChannelsList = useInvalidateChannelsList();

  const confirmPasswordMutation = useMutation<
    void,
    ApiError,
    IConfirmPasswordTelegramPersonalDto
  >({
    mutationFn: (data: IConfirmPasswordTelegramPersonalDto) =>
      sendTgPersonalPassword(data.channelId, { password: data.password }),
    async onSettled() {
      await invalidateChannelsList();
    },
  });

  const confirmPassword = async (
    data: IConfirmPasswordTelegramPersonalDto,
    onCreated?: () => void
  ) => {
    return confirmPasswordMutation.mutateAsync(data, {
      onSuccess: onCreated,
    });
  };

  return { confirmPassword, ...confirmPasswordMutation };
}

export function useConfirmPasswordExternal() {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();

  const confirmExternalPasswordMutation = useMutation({
    mutationFn: (data: IConfirmPasswordTelegramPersonalDto) =>
      privateSendTgPersonalPassword(data.channelId, {
        password: data.password,
      }),
    onError: (error) => {
      addNotification({
        title: error.message || t('common:errors.genericTitle'),
        caption: error?.data?.details || t('common:errors.generic'),
      });
    },
  });

  const confirmPasswordExternal = async (
    data: IConfirmPasswordTelegramPersonalDto,
    onSuccess?: () => void
  ) => {
    return confirmExternalPasswordMutation.mutateAsync(data, {
      onSuccess,
    });
  };

  return { confirmPasswordExternal, ...confirmExternalPasswordMutation };
}
